<template>
  <div id="listWidget" class="mb-5">
    <!-- 사용 안내 -->
    <div class="instruction-card card-item mb-3">
      <div class="instruction-header">
        <i class="fa-solid fa-info-circle me-2 text-primary"></i>
        <span class="instruction-title">사용 안내</span>
      </div>
      <div class="instruction-body">
        <p class="mb-0" style="font-size: 0.85rem; line-height: 1.6; color: #495057;">
          가상버튼을 수정해주세요. 포함할 장치의 <code style="font-size: 100%">포함</code>/<code style="font-size: 100%">미포함</code>과
          상태값을 설정하고 <code style="font-size: 100%">수정 버튼</code>을 클릭하면 설정된 상태값으로 가상버튼이 수정됩니다.
        </p>
        <p class="mb-0 mt-2" style="font-size: 0.8rem; line-height: 1.5; color: #6c757d;">
          다른 방의 조명·커튼도 아래에서 방 이름을 눌러 펼친 뒤
          <code style="font-size: 100%">포함</code>/<code style="font-size: 100%">미포함</code>으로 선택할 수 있습니다.
        </p>
      </div>
    </div>

    <!-- 장치 이름 + 수정 버튼 -->
    <div class="name-edit-card card-item mb-3" v-if="realDevices.length > 0 || selectedRoomLights.length > 0 || selectedOtherLights.length > 0 || selectedCurtainDevices.length > 0 || otherRoomGroups.length > 0 || absorbedHostDevice">
      <div class="name-edit-body d-flex align-items-center">
        <div class="menu-search-input flex-fill" style="padding-right: 10px">
          <input
            type="text"
            maxlength="20"
            class="form-control"
            style="padding-right: 50px"
            placeholder="장치 이름을 입력해주세요."
            v-model="deviceName"
            readonly
          />
        </div>
        <button class="btn btn-primary" @click="onUpdateClick">수정</button>
      </div>
    </div>

    <!-- 조명 (현재 방) -->
    <div class="section-card card-item mb-3" v-if="roomLightDevicesDisplay.length > 0">
      <div class="section-header">
        <i class="fa-regular fa-lightbulb me-2 text-primary"></i>
        <span class="section-title">조명 ( {{ group?.name }} )</span>
      </div>
      <div class="section-body">
        <div class="row">
          <VirtualDeviceItem
            v-for="item in roomLightDevicesDisplay"
            :key="item._id"
            :group="group"
            :device="item"
            :deviceType="1"
            optional-include
            :include-locked="!!item._absorbFixed"
            :selectedDevices="selectedRoomLights"
            @onDeviceChecked="handleRoomLightChecked"
            @snapshotChanged="handleAbsorbSnapshotChanged"
          />
        </div>
      </div>
    </div>

    <!-- 커튼 (현재 방) -->
    <div class="section-card card-item mb-3" v-if="curtainDevices.length > 0">
      <div class="section-header">
        <img :src="require('@/assets/images/curtain.svg')" class="section-header-icon me-2" alt="" />
        <span class="section-title">커튼 ( {{ group?.name }} )</span>
      </div>
      <div class="section-desc-block">
        <i class="fa-solid fa-info-circle me-2 text-primary section-desc-icon"></i>
        <span class="section-desc">가상버튼이 ON일 때 포함한 커튼의 열림/닫힘 정도가 적용됩니다.</span>
      </div>
      <div class="section-body">
        <div class="row">
          <VirtualDeviceItem
            v-for="item in curtainDevices"
            :key="item._id"
            :group="group"
            :device="item"
            :deviceType="item.type"
            :selectedDevices="selectedCurtainDevices"
            @onDeviceChecked="handleCurtainChecked"
          />
        </div>
      </div>
    </div>

    <!-- 다른 방 (조명 + 커튼) -->
    <div
      v-for="og in otherRoomGroups"
      :key="og._id"
      class="section-card card-item mb-3"
    >
      <div
        class="section-header section-header-toggle"
        :class="{ 'section-header-collapsed': !isOtherRoomExpanded(og._id) }"
        role="button"
        @click="toggleOtherRoom(og._id)"
      >
        <i class="fa-brands fa-microsoft me-2 text-primary"></i>
        <span class="section-title flex-fill">{{ og.name }}</span>
        <span
          v-if="selectedCountInGroup(og) > 0"
          class="other-room-badge me-2"
        >포함 {{ selectedCountInGroup(og) }}</span>
        <i
          :class="`fa-solid ${isOtherRoomExpanded(og._id) ? 'fa-chevron-up' : 'fa-chevron-down'} text-muted`"
          style="font-size: 0.85rem;"
        ></i>
      </div>
      <div v-if="isOtherRoomExpanded(og._id)" class="section-body">
        <div class="row" v-if="physicalLampsDisplayOf(og).length > 0">
          <VirtualDeviceItem
            v-for="item in physicalLampsDisplayOf(og)"
            :key="item._id"
            :group="og"
            :device="item"
            deviceType="1"
            optional-include
            :include-locked="!!item._absorbFixed"
            :selectedDevices="selectedOtherLights"
            @onDeviceChecked="handleOtherLightChecked"
            @snapshotChanged="handleAbsorbSnapshotChanged"
          />
        </div>
        <div
          v-if="curtainDevicesOf(og).length > 0"
          class="section-desc-block"
          :class="{ 'mt-2': physicalLampsDisplayOf(og).length > 0 }"
          style="margin-left: -1rem; margin-right: -1rem;"
        >
          <i class="fa-solid fa-info-circle me-2 text-primary section-desc-icon"></i>
          <span class="section-desc">커튼 · 포함 시 가상버튼 ON 때 열림 정도가 적용됩니다.</span>
        </div>
        <div class="row" v-if="curtainDevicesOf(og).length > 0">
          <VirtualDeviceItem
            v-for="item in curtainDevicesOf(og)"
            :key="item._id"
            :group="og"
            :device="item"
            :deviceType="item.type"
            :selectedDevices="selectedCurtainDevices"
            @onDeviceChecked="handleCurtainChecked"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref,
  reactive,
  getCurrentInstance,
  onBeforeMount,
  computed,
  onMounted,
  onBeforeUnmount,
  onUnmounted,
  watch,
  nextTick
} from 'vue';
import Axios, { DeviceApi, VirtualDeviceApi } from '@/api';
import {
  useRouter,
  useRoute,
  onBeforeRouteLeave,
  onBeforeRouteUpdate,
} from 'vue-router';
import { useStore } from 'vuex';
import PopDialog from '@/common/components/popup/PopDialog.vue';
import useModal from '@/common/hooks/useModal';
import VirtualDeviceItem from './components/VirtualDeviceItem.vue';
import useMqttControl from '@/common/hooks/useMqttControl';
import { MQTTCurtainCommand, MQTTDooyaRonoCommand, MQTTDooyaRonoAction } from '@/common/constants';
import { toDisplayLampPort } from '@/common/utils/controllerDisplayName';

const mqttControl = reactive(useMqttControl());
const { emitter } = getCurrentInstance().appContext.config.globalProperties;
const router = useRouter();
const route = useRoute();
const store = useStore();
const deviceName = ref('');
const virtualDevices = ref([]);
const srcDevices = ref([]);
const selectedCurtainDevices = ref([]);
const selectedRoomLights = ref([]);
const selectedOtherLights = ref([]);
const expandedOtherRoomIds = ref([]);
let rollbackStatus = true;
let otherLightsInitialized = false;

const zones = computed(() => {
  return store.getters['home/getZones'];
});

const groups = computed(() => {
  const groups = [];
  zones?.value?.forEach((zone) => groups.push(...zone.groups));
  return groups;
});

const group = computed(() => {
  return groups?.value?.find((g) => g._id == route.params.groupId);
});

const currentZone = computed(() => {
  const gid = String(route.params.groupId);
  return zones.value?.find((z) => (z.groups || []).some((g) => String(g._id) === gid));
});

const physicalLampsOf = (g) => {
  return (g?.devices || []).filter(
    (d) => d.type === 1 && (d.isVirtual === undefined || d.isVirtual === false),
  );
};

const curtainDevicesOf = (g) => {
  return (g?.devices || []).filter(
    (d) =>
      (d.type === 3 || d.type === 5 || d.type === 6) &&
      (d.isVirtual === undefined || d.isVirtual === false),
  );
};

const firstPortOf = (device) => {
  const ports = device?.item?.ports;
  if (Array.isArray(ports) && ports.length > 0) return Number(ports[0]);
  if (ports != null) return Number(ports);
  return NaN;
};

/** 흡수된 물리 호스트 씬 스냅샷 (삭제된 Device — UI 고정 포함용) */
const absorbMeta = computed(() => device.value?.boundButtonAbsorb || null);

const absorbedHostSceneItem = computed(() => {
  const meta = absorbMeta.value;
  if (!meta || meta.controllerId == null || meta.port == null) return null;
  const port = Number(meta.port);
  const serial = String(meta.controllerId);
  const absorbedIds = new Set((meta.absorbedMemberIds || []).map(String));
  const items = virtualDevices.value || [];
  let snap =
    items.find((it) => {
      if (!absorbedIds.has(String(it._id))) return false;
      return String(it.controllerId) === serial && firstPortOf(it) === port;
    }) ||
    items.find(
      (it) => String(it.controllerId) === serial && firstPortOf(it) === port,
    );
  if (!snap && meta.hostItem) {
    snap = {
      _id: `absorb-host-${serial}-${port}`,
      controllerId: serial,
      name: meta.hostName || `${toDisplayLampPort(port, serial)}번`,
      type: 1,
      isVirtual: false,
      item: {
        ports: Array.isArray(meta.hostItem.ports) && meta.hostItem.ports.length
          ? meta.hostItem.ports.map(Number)
          : [port],
        bright: meta.hostItem.bright != null ? Number(meta.hostItem.bright) : 50,
        color: meta.hostItem.color != null ? Number(meta.hostItem.color) : 10,
        onoff: 1,
      },
    };
  }
  return snap || null;
});

const absorbedHostDevice = computed(() => {
  const meta = absorbMeta.value;
  const snap = absorbedHostSceneItem.value;
  if (!meta || !snap) return null;
  const port = Number(meta.port);
  return {
    ...snap,
    _absorbFixed: true,
    name: meta.hostName || snap.name || `${toDisplayLampPort(port, String(meta.controllerId))}번`,
    type: 1,
    isVirtual: false,
    controllerId: String(meta.controllerId),
    item: {
      ...(snap.item || {}),
      ports:
        Array.isArray(snap.item?.ports) && snap.item.ports.length
          ? snap.item.ports.map(Number)
          : [port],
      onoff: 1,
      bright: snap.item?.bright != null ? Number(snap.item.bright) : 50,
      color: snap.item?.color != null ? Number(snap.item.color) : 10,
    },
  };
});

const absorbBelongsToCurrentRoom = computed(() => {
  const meta = absorbMeta.value;
  if (!meta || !absorbedHostDevice.value) return false;
  if (meta.groupId == null) return true;
  return String(meta.groupId) === String(route.params.groupId);
});

const roomLightDevicesDisplay = computed(() => {
  const list = [...(roomLightDevices.value || [])];
  const abs = absorbedHostDevice.value;
  if (!abs || !absorbBelongsToCurrentRoom.value) return list;
  if (list.some((d) => String(d._id) === String(abs._id))) return list;
  if (
    list.some(
      (d) =>
        String(d.controllerId) === String(abs.controllerId) &&
        firstPortOf(d) === firstPortOf(abs),
    )
  ) {
    return list;
  }
  return [abs, ...list];
});

const physicalLampsDisplayOf = (g) => {
  const list = [...physicalLampsOf(g)];
  const abs = absorbedHostDevice.value;
  const meta = absorbMeta.value;
  if (!abs || !meta || meta.groupId == null) return list;
  if (String(meta.groupId) !== String(g?._id)) return list;
  if (String(g?._id) === String(route.params.groupId)) return list;
  if (list.some((d) => String(d._id) === String(abs._id))) return list;
  return [abs, ...list];
};

const otherRoomGroups = computed(() => {
  const gid = String(route.params.groupId);
  return (currentZone.value?.groups || []).filter((g) => {
    if (String(g._id) === gid) return false;
    const hasLive =
      physicalLampsOf(g).length > 0 || curtainDevicesOf(g).length > 0;
    const hasAbsorb =
      absorbedHostDevice.value &&
      absorbMeta.value?.groupId != null &&
      String(absorbMeta.value.groupId) === String(g._id);
    return hasLive || hasAbsorb;
  });
});

const allOtherRoomLamps = computed(() => {
  const list = [];
  for (const og of otherRoomGroups.value) {
    list.push(...physicalLampsOf(og));
  }
  return list;
});

const allOtherRoomCurtains = computed(() => {
  const list = [];
  for (const og of otherRoomGroups.value) {
    list.push(...curtainDevicesOf(og));
  }
  return list;
});

const devices = computed(() => {
  return group?.value?.devices || [];
});

const device = computed(() => {
  return devices.value?.find((d) => d._id == route.params.virtualId);
});

const curtainDevices = computed(() => {
  return (group?.value?.devices || []).filter(
    (item) =>
      (item.type == 3 || item.type == 5 || item.type == 6) &&
      (item.isVirtual === undefined || item.isVirtual === false),
  );
});

const realDevices = computed(() => {
  return devices.value?.filter(item => item.isVirtual === undefined || item.isVirtual === false) || [];
});

const roomLightDevices = computed(() => {
  return realDevices.value?.filter((d) => d.type == 1) || [];
});

const memberIdSet = computed(() => {
  return new Set((virtualDevices.value || []).map((v) => String(v._id)));
});

const targetDevices = computed(() => {
  return realDevices?.value?.filter((d => memberIdSet.value.has(String(d._id)))) || [];
});

const targetLightDevices = computed(() => {
  return roomLightDevices.value.filter((d) => memberIdSet.value.has(String(d._id)));
});

const isOtherRoomExpanded = (groupId) => expandedOtherRoomIds.value.includes(String(groupId));

const toggleOtherRoom = (groupId) => {
  const sid = String(groupId);
  if (expandedOtherRoomIds.value.includes(sid)) {
    expandedOtherRoomIds.value = expandedOtherRoomIds.value.filter((id) => id !== sid);
  } else {
    expandedOtherRoomIds.value = [...expandedOtherRoomIds.value, sid];
  }
};

const selectedCountInGroup = (og) => {
  const lamps = physicalLampsDisplayOf(og);
  const lampIds = new Set(lamps.map((d) => String(d._id)));
  const curtainIds = new Set(curtainDevicesOf(og).map((d) => String(d._id)));
  const lightCount = selectedOtherLights.value.filter((d) => lampIds.has(String(d._id))).length;
  const curtainCount = selectedCurtainDevices.value.filter((d) => curtainIds.has(String(d._id))).length;
  return lightCount + curtainCount;
};

const liveDeviceFromStore = (deviceId) => {
  const sid = String(deviceId);
  for (const z of zones.value || []) {
    for (const g of z.groups || []) {
      const found = (g.devices || []).find((d) => String(d._id) === sid);
      if (found) return found;
    }
  }
  return null;
};

const resolveSelectedRoomLights = () => {
  return selectedRoomLights.value.map((sel) => {
    const live = liveDeviceFromStore(sel._id);
    return live ? { ...live } : { ...sel };
  });
};

const resolveSelectedOtherLights = () => {
  return selectedOtherLights.value.map((sel) => {
    const live = liveDeviceFromStore(sel._id);
    return live ? { ...live } : { ...sel };
  });
};

const resolveSelectedCurtains = () => {
  return selectedCurtainDevices.value.map((sel) => {
    const live = liveDeviceFromStore(sel._id);
    return live ? { ...live } : { ...sel };
  });
};

const syncSelectedMembersFromVirtual = () => {
  const selectedLamps = [];
  const selectedCurtains = [];
  const expandIds = new Set();
  for (const og of otherRoomGroups.value) {
    const lamps = physicalLampsOf(og).filter((d) => memberIdSet.value.has(String(d._id)));
    const curtains = curtainDevicesOf(og).filter((d) => memberIdSet.value.has(String(d._id)));
    if (lamps.length > 0 || curtains.length > 0) {
      selectedLamps.push(...lamps);
      selectedCurtains.push(...curtains);
      expandIds.add(String(og._id));
    }
  }
  const currentLights = roomLightDevices.value.filter((d) =>
    memberIdSet.value.has(String(d._id)),
  );
  const currentCurtains = curtainDevices.value.filter((d) =>
    memberIdSet.value.has(String(d._id)),
  );
  const abs = absorbedHostDevice.value;
  if (abs) {
    if (absorbBelongsToCurrentRoom.value) {
      if (!currentLights.some((d) => String(d._id) === String(abs._id))) {
        currentLights.unshift(abs);
      }
    } else if (absorbMeta.value?.groupId != null) {
      if (!selectedLamps.some((d) => String(d._id) === String(abs._id))) {
        selectedLamps.unshift(abs);
      }
      expandIds.add(String(absorbMeta.value.groupId));
    }
  }
  selectedRoomLights.value = currentLights;
  selectedOtherLights.value = selectedLamps;
  selectedCurtainDevices.value = [...currentCurtains, ...selectedCurtains];
  expandedOtherRoomIds.value = [...expandIds];
  otherLightsInitialized = true;
};

watch([virtualDevices, otherRoomGroups, curtainDevices], () => {
  if (!otherLightsInitialized && (virtualDevices.value || []).length > 0) {
    syncSelectedMembersFromVirtual();
  }
}, { immediate: true });

const handleCurtainChecked = (data) => {
  if (data.isChecked) {
    const exists = selectedCurtainDevices.value.some(
      (device) => String(device._id) === String(data.device._id),
    );
    if (!exists) {
      selectedCurtainDevices.value.push(data.device);
    }
  } else {
    selectedCurtainDevices.value = selectedCurtainDevices.value.filter(
      (device) => String(device._id) !== String(data.device._id),
    );
  }
};

const handleRoomLightChecked = (data) => {
  if (data.device?._absorbFixed && !data.isChecked) {
    return;
  }
  if (data.isChecked) {
    const exists = selectedRoomLights.value.some((d) => String(d._id) === String(data.device._id));
    if (!exists) {
      selectedRoomLights.value.push(data.device);
    }
  } else {
    selectedRoomLights.value = selectedRoomLights.value.filter(
      (d) => String(d._id) !== String(data.device._id),
    );
  }
};

const handleOtherLightChecked = (data) => {
  if (data.device?._absorbFixed && !data.isChecked) {
    return;
  }
  if (data.isChecked) {
    const exists = selectedOtherLights.value.some((d) => String(d._id) === String(data.device._id));
    if (!exists) {
      selectedOtherLights.value.push(data.device);
    }
  } else {
    selectedOtherLights.value = selectedOtherLights.value.filter(
      (d) => String(d._id) !== String(data.device._id),
    );
  }
};

/** 흡수 호스트 밝기/색온도 — selected·virtualDevices 스냅샷에 반영 (수정 저장용) */
const handleAbsorbSnapshotChanged = (data) => {
  if (!data?.device?._absorbFixed) return;
  const bright = Number(data.bright);
  const color = Number(data.color);
  const sid = String(data.device._id);
  const patchItem = (d) => {
    if (String(d._id) !== sid) return d;
    return {
      ...d,
      item: {
        ...(d.item || {}),
        bright: Number.isNaN(bright) ? d.item?.bright : bright,
        color: Number.isNaN(color) ? d.item?.color : color,
        onoff: 1,
      },
    };
  };
  selectedRoomLights.value = selectedRoomLights.value.map(patchItem);
  selectedOtherLights.value = selectedOtherLights.value.map(patchItem);
  virtualDevices.value = (virtualDevices.value || []).map(patchItem);
};

const lampPortsOf = (it) => {
  const ports = it?.item?.ports;
  if (Array.isArray(ports)) return ports.map(Number).filter((p) => Number.isFinite(p));
  if (ports != null && Number.isFinite(Number(ports))) return [Number(ports)];
  return [];
};

/**
 * 수정 화면 이탈(뒤로/홈/다른 메뉴) 시 정리 대상.
 * - 조명: 진입·편집 중 미리보기로 켠 포트 강제 소등 (흡수로 삭제된 호스트 포함)
 * - 커튼/모터: 진입 직전 srcDevices 스냅샷 percent로 복원 (씬 미리보기 위치 되돌림)
 */
const buildEditLeaveDevices = () => {
  const lamps = [];
  const seenPort = new Set();
  const addLampOff = (d) => {
    if (!d || Number(d.type) !== 1 || !d.controllerId) return;
    const ports = lampPortsOf(d);
    if (ports.length === 0) return;
    const key = `${d.controllerId}|${ports.join(',')}`;
    if (seenPort.has(key)) return;
    seenPort.add(key);
    lamps.push({
      ...d,
      type: 1,
      item: {
        ...(d.item || {}),
        ports,
        onoff: 0,
        bright: d.item?.bright != null ? parseInt(d.item.bright, 10) : 0,
        color: d.item?.color ?? 0,
      },
    });
  };

  for (const d of virtualDevices.value || []) addLampOff(d);
  for (const d of selectedRoomLights.value || []) addLampOff(d);
  for (const d of selectedOtherLights.value || []) addLampOff(d);
  if (absorbedHostDevice.value) addLampOff(absorbedHostDevice.value);

  // 미리보기로 움직인 커튼만 진입 스냅샷으로 복원
  const srcById = new Map(
    (srcDevices.value || [])
      .filter((d) => d.type == 3 || d.type == 5 || d.type == 6)
      .map((d) => [String(d._id), d]),
  );
  const curtainIds = new Set();
  for (const d of virtualDevices.value || []) {
    if (d.type == 3 || d.type == 5 || d.type == 6) curtainIds.add(String(d._id));
  }
  for (const d of selectedCurtainDevices.value || []) {
    if (d.type == 3 || d.type == 5 || d.type == 6) curtainIds.add(String(d._id));
  }
  const curtains = [];
  for (const id of curtainIds) {
    const snap = srcById.get(id);
    if (snap) curtains.push(snap);
  }
  return [...lamps, ...curtains];
};

const publishStatus = async (devices) => {
  console.log("devices:", devices)

  const lampItems = (devices || []).filter((d) => d.type == 1);
  const byController = {};
  for (const it of lampItems) {
    const cid = it.controllerId;
    if (!byController[cid]) byController[cid] = [];
    byController[cid].push(it);
  }
  for (const controllerId of Object.keys(byController)) {
    const group = byController[controllerId];
    const portsArr = [];
    const onoffArr = [];
    const brightArr = [];
    const colorArr = [];
    const deviceIds = [];
    for (const it of group) {
      deviceIds.push(it._id);
      const ports = lampPortsOf(it);
      const onoff = it.item?.onoff ?? 0;
      const bright = it.item?.bright != null ? parseInt(it.item.bright, 10) : 0;
      const color = it.item?.color ?? 0;
      for (const p of ports) {
        portsArr.push(p);
        onoffArr.push(onoff);
        brightArr.push(bright);
        colorArr.push(color);
      }
    }
    if (portsArr.length > 0) {
      mqttControl.onPublishBatch(controllerId, deviceIds, {
        serial: controllerId,
        cmd: 'cs',
        type: 1,
        data: { ports: portsArr, onoff: onoffArr, bright: brightArr, color: colorArr },
      });
    }
  }

  for (const device of devices || []) {
    if (device.type == 3) {
      mqttControl.onPublishTemp(device.controllerId, MQTTCurtainCommand.IsSetClosePercent, `${device.item.address},${device.item.percent}`);
      await requestUpdateDevice(device._id, {
        item: { percent: device.item.percent }
      });
    } else if (device.type == 5) {
      mqttControl.onPublish(device.controllerId, device._id, {
        cmd: MQTTDooyaRonoCommand.DooyaCtrl,
        type: 1,
        data: {
          addr: parseInt(device.item.address, 10),
          action: MQTTDooyaRonoAction.Percent,
          percent: parseInt(device.item.percent, 10),
        }
      });
      await requestUpdateDevice(device._id, {
        item: { percent: device.item.percent }
      });
    } else if (device.type == 6) {
      mqttControl.onPublish(device.controllerId, device._id, {
        cmd: MQTTDooyaRonoCommand.RonoCtrl,
        type: 1,
        data: {
          addr: parseInt(device.item.address, 10),
          action: MQTTDooyaRonoAction.Percent,
          percent: parseInt(device.item.percent, 10),
        }
      });
      await requestUpdateDevice(device._id, {
        item: { percent: device.item.percent }
      });
    }
  }
};

const requestVirtualButton = async () => {
  try {
    const result = await Axios.get(VirtualDeviceApi.VIRTUAL_DEVICE.replace(':id', route.params.virtualId));
    if (result.resultCode === '0000') {
      const raw = result.resultData?.items;
      virtualDevices.value = Array.isArray(raw) ? raw : Object.values(raw || {});
      otherLightsInitialized = false;
      syncSelectedMembersFromVirtual();
      nextTick(async () => {
        srcDevices.value = JSON.parse(JSON.stringify([
          ...targetLightDevices.value,
          ...allOtherRoomLamps.value,
          ...curtainDevices.value,
          ...allOtherRoomCurtains.value,
        ]));
        deviceName.value = device.value.name || "";
        await publishStatus(virtualDevices.value);
      });
      return;
    }

    emitter.emit('showPopAlert', {
      description: result.resultMessage,
      okCallback: () => router.go(-1),
      cancelCallback:() => router.go(-1)
    });
  } catch (e) {
    console.log(e);
    emitter.emit('showPopAlert', {
      description: '가상버튼 조회 중 오류가 발생하였습니다.',
      okCallback: () => router.go(-1),
      cancelCallback:() => router.go(-1)
    });
  }
};

onMounted(() => {
  requestVirtualButton();
});

// 페이지를 떠나기 전: 수정 미리보기로 켠 조명 강제 소등(+커튼은 진입 스냅샷 복원)
onBeforeRouteLeave(async (to, from, next) => {
  try {
    console.log('페이지를 떠나기 전 미리보기 소등 중...');
    await publishStatus(buildEditLeaveDevices());
    console.log('미리보기 소등 완료');
  } catch (error) {
    console.error('미리보기 소등 실패:', error);
  }
  next();
});

onBeforeUnmount(() => {
  // onBeforeUnmount에서는 API 호출 제거
  // onBeforeRouteLeave에서 처리하므로 여기서는 불필요
});

const onUpdateClick = () => {
  if (deviceName.value.length === 0) {
    emitter.emit('showPopAlert', {
      description: '장치 이름을 선택해주세요.',
    });
    return;
  }

  const roomLights = resolveSelectedRoomLights().filter((d) => !d._absorbFixed);
  const otherLights = resolveSelectedOtherLights().filter((d) => !d._absorbFixed);
  const includedCurtains = resolveSelectedCurtains();
  const absorbSelected =
    selectedRoomLights.value.find((d) => d._absorbFixed) ||
    selectedOtherLights.value.find((d) => d._absorbFixed) ||
    null;
  const absorbSource = absorbSelected || absorbedHostSceneItem.value;
  const absorbSnap = absorbSource
    ? (() => {
        const { _absorbFixed, ...rest } = absorbSource;
        return {
          ...rest,
          item: {
            ...(absorbSource.item || {}),
            onoff: 1,
            bright:
              absorbSource.item?.bright != null ? Number(absorbSource.item.bright) : 50,
            color:
              absorbSource.item?.color != null ? Number(absorbSource.item.color) : 10,
          },
        };
      })()
    : null;
  if (
    !absorbSnap &&
    roomLights.length === 0 &&
    otherLights.length === 0 &&
    includedCurtains.length === 0
  ) {
    emitter.emit('showPopAlert', {
      description: '포함할 조명 또는 커튼을 1개 이상 선택해주세요.',
    });
    return;
  }
  const params = {
    items: [
      ...(absorbSnap ? [absorbSnap] : []),
      ...roomLights,
      ...otherLights,
      ...includedCurtains,
    ],
    name: deviceName.value
  };
  console.log("params:", params);
  requestUpdateVirtualDevice(params);
};

const requestUpdateVirtualDevice = async (payload) => {
  try {
    const result = await Axios.patch(
      VirtualDeviceApi.UPDATE_VIRTUAL_DEVICE.replace(':id', route.params.virtualId),
      payload,
    );
    if (result.resultCode === '0000') {
      rollbackStatus = !(device.value.isVirtual && device.value.item.onoff);
      emitter.emit('showPopAlert', {
        description: '가상 버튼이 수정되었습니다.',
        okCallback: () => router.go(-1),
        cancelCallback:() => router.go(-1)
      });
      return;
    }

    emitter.emit('showPopAlert', {
      description: result.resultMessage,
    });
  } catch (e) {
    console.log(e);
    emitter.emit('showPopAlert', {
      description: '가상버튼 수정 중 오류가 발생하였습니다.',
    });
  }
}; 

const requestUpdateDevice = async (id, params) => {
  return new Promise(async (resolve, reject) => {
    try {
      const result = await Axios.patch(DeviceApi.UPDATE_DEVICE.replace(':id', id), params);
      if (result.resultCode === '0000') {
        store.commit('home/updateDevice', {
          groupId: group.value._id,
          id,
          data: params,
        });
        resolve(result);
      } else {
        // emitter.emit('showPopAlert', {
        //   description: result.resultMessage,
        // });
        reject(result.resultMessage);
      }
    } catch (error) {
      // emitter.emit('showPopAlert', {
      //   description: '장치 정보 수정에 실패했습니다.',
      // });
      reject('장치 정보 수정에 실패했습니다.');
    }
  });
};
</script>

<style scoped>
/* instruction-card 스타일 (LightControllersSettingPage와 동일) */
.instruction-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.instruction-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.instruction-header {
  display: flex;
  align-items: center;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.instruction-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.instruction-body {
  padding: 0.75rem 1rem;
}

/* 장치 이름 + 수정 버튼 카드 (LightControllersSettingPage 카드 컨셉) */
.name-edit-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.name-edit-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.name-edit-body {
  padding: 0.75rem 1rem;
}

/* 조명/커튼 섹션 카드 (LightControllersSettingPage 카드 컨셉) */
.section-card {
  background-color: #ffffff;
  border: 1px solid #e9ecef;
  border-radius: 1.2rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  transition: box-shadow 0.3s ease;
}

.section-card:hover {
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.section-header {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
}

.section-header-icon {
  height: 1em;
  width: auto;
  vertical-align: middle;
  /* SVG를 검정으로 만든 뒤 Bootstrap primary(#0d6efd)로 통일 - 조명 아이콘과 동일 톤 */
  filter: brightness(0) saturate(100%) invert(40%) sepia(99%) saturate(3200%) hue-rotate(216deg) brightness(94%) contrast(102%);
}

.section-title {
  font-size: 0.9rem;
  font-weight: 600;
  color: #212529;
}

.section-desc-block {
  display: flex;
  align-items: flex-start;
  gap: 0;
  padding: 0.4rem 1rem;
  border-bottom: 1px solid #e9ecef;
}

.section-desc-icon {
  font-size: 0.85rem;
  flex-shrink: 0;
  margin-top: 0.1rem;
}

.section-desc {
  font-size: 0.85rem;
  line-height: 1.6;
  color: #495057;
  word-break: keep-all;
}

.section-body {
  padding: 0.75rem 1rem;
}

.section-header-toggle {
  cursor: pointer;
  user-select: none;
}

.section-header-collapsed {
  border-bottom: none;
}

.other-room-badge {
  font-size: 0.75rem;
  font-weight: 600;
  color: #0d6efd;
  background: #e7f1ff;
  border-radius: 999px;
  padding: 0.15rem 0.5rem;
}
</style>
